import {
  AverageTransactionValue,
  BusinessActivity,
  CountryCode,
  ExpectedMonthlyRevenue,
  PartyType,
  RelationType,
  SourceOfIncome,
} from "Infrastructure/Api/Api";
import { Resources, type TFunction } from "Translations/Resources";
import { z } from "zod";

export const getSchema = (
  t: TFunction,
  isHunter: boolean,
  partyType?: PartyType,
) =>
  z
    .object({
      businessActivity: z.enum(BusinessActivity),
      otherBusinessActivityDetails: z.string().nullish(), // required when businessActivity is Other, optional otherwise
      sourcesOfIncome: z.array(z.enum(SourceOfIncome)).min(1),
      otherSourceOfIncomeDetails: z.string().nullish(), // required when OtherBusinessIncome is selected, optional otherwise
      expectedMonthlyRevenue: isHunter
        ? z.enum(ExpectedMonthlyRevenue).nullish()
        : z.enum(ExpectedMonthlyRevenue),
      averageTransactionValue: isHunter
        ? z.enum(AverageTransactionValue).nullish()
        : z.enum(AverageTransactionValue),
      taxResidency: z.enum(CountryCode),
      relationType:
        partyType === PartyType.LegalEntity
          ? z.enum(RelationType)
          : z.enum(RelationType).optional(),
      isPoliticallyExposedPerson: z.boolean(),
      hasNonEUResidence: z.boolean().nullish(),
      hasNonEUNationality: z.boolean().nullish(),
    })
    .superRefine((data, ctx) => {
      // Issues raised here carry the whole form object as their input, so a synthetic
      // "invalid_type" is worded by the locale as "expected string, received object". Say
      // what the user needs to hear instead.
      if (
        data.businessActivity === BusinessActivity.OtherAndPublicServices &&
        !data.otherBusinessActivityDetails
      ) {
        ctx.addIssue({
          code: "custom",
          message: t(Resources.ValidationMessages.Required),
          path: ["otherBusinessActivityDetails"],
        });
      }
      if (
        data.sourcesOfIncome.includes(SourceOfIncome.OtherBusinessIncome) &&
        !data.otherSourceOfIncomeDetails
      ) {
        ctx.addIssue({
          code: "custom",
          message: t(Resources.ValidationMessages.Required),
          path: ["otherSourceOfIncomeDetails"],
        });
      }
    });

export type IncomeInformationStepFormData = z.infer<
  ReturnType<typeof getSchema>
>;
